import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    DxChartModule,
    DxSelectBoxModule,
} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    AggregateType,
    DataSources,
    ExportMyScoreVendorsQuery,
    GetMyScoreVendorsScoreQuery,
    GetMyScoreVendorsTrendingQuery,
    InvoicePurchaseOrderStatus,
    InvoicingClient,
    PermissionType,
} from '@services';
import EnumUtils from '../../../../shared/enum-utils';
import {FileUtils} from '../../../../shared/file-utils';
import {DoughnutChartTableComponent} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {MyScoreFilterBarComponent, MyScoreFilters} from '../my-score-filter-bar/my-score-filter-bar.component';

type ChartSlice = { argument: string; value: number };
type TrendingPoint = { day: Date; value: number };

@Component({
    selector: 'app-my-score-vendors',
    imports: [
        CommonModule,
        DoughnutChartTableComponent,
        MyScoreFilterBarComponent,
        DxChartModule,
        DxSelectBoxModule,
    ],
    templateUrl: './my-score-vendors.component.html',
})
export class MyScoreVendorsComponent implements OnInit {
    private invoicingClient = inject(InvoicingClient);
    private dataSources = inject(DataSources);
    private destroyRef = inject(DestroyRef);

    private static readonly StatusLabels: { status: InvoicePurchaseOrderStatus; label: string }[] = [
        {status: InvoicePurchaseOrderStatus.Open, label: 'Open'},
        {status: InvoicePurchaseOrderStatus.InvoiceMissed, label: 'Invoice Missed'},
        {status: InvoicePurchaseOrderStatus.InvoicePending, label: 'Invoiced On Time'},
        {status: InvoicePurchaseOrderStatus.InvoicedLate, label: 'Invoiced Late'},
        {status: InvoicePurchaseOrderStatus.Posted, label: 'Posted'},
    ];

    protected palette: Record<string, string> = {};
    protected trendingColor = '';

    protected vendorDataSource = this.dataSources.vendorOptions();
    protected siteDataSource = this.dataSources.siteOptions(PermissionType.MyScoreVendors);

    protected aggregateType = AggregateType.Day;
    protected aggregateOptions = EnumUtils.getEnumOptions(AggregateType);

    protected slices: ChartSlice[] = [];
    protected myScorePercent: number | null = null;
    protected trendingData: TrendingPoint[] = [];

    private filters?: MyScoreFilters;

    ngOnInit(): void {
        const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
        this.palette = {
            'Open': getColor('--bs-secondary'),
            'Invoice Missed': getColor('--bs-danger'),
            'Invoiced On Time': getColor('--bs-info'),
            'Invoiced Late': getColor('--bs-warning'),
            'Posted': getColor('--bs-success'),
        };
        this.trendingColor = getColor('--bs-info');
    }

    protected onFiltersChanged(filters: MyScoreFilters): void {
        this.filters = filters;
        this.loadScore();
        this.loadTrending();
    }

    protected onAggregateChanged(): void {
        this.loadTrending();
    }

    protected onExport(): void {
        if (!this.filters) return;

        this.invoicingClient
            .exportMyScoreVendors(new ExportMyScoreVendorsQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => FileUtils.downloadFile(res));
    }

    protected customizeTrendingTooltip = (point: { value: number }) => ({ text: `${point.value}%` });

    private loadScore(): void {
        if (!this.filters) return;

        this.invoicingClient
            .getMyScoreVendorsScore(new GetMyScoreVendorsScoreQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                const totals = dto.totals ?? {};
                this.slices = MyScoreVendorsComponent.StatusLabels.map(({status, label}) => ({
                    argument: label,
                    value: totals[status] ?? 0,
                }));
                this.myScorePercent = dto.myScorePercent ?? null;
            });
    }

    private loadTrending(): void {
        if (!this.filters) return;

        this.invoicingClient
            .getMyScoreVendorsTrending(new GetMyScoreVendorsTrendingQuery({
                ...this.filters,
                aggregateType: this.aggregateType,
            }))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                this.trendingData = Object.entries(dto.timeSeries ?? {})
                    .map(([key, value]) => ({ day: new Date(key), value }))
                    .sort((a, b) => a.day.getTime() - b.day.getTime());
            });
    }
}
